import type { AssetTreeSearch } from "./assetTreeSearch";
import {
  type RetainedListModule,
  type RetainedListValues,
  retainedListCodecs,
} from "./retainedListModules";

/**
 * Per-session memory of a module list's view state, so primary navigation and
 * the Module breadcrumb can reopen a list the way the user left it. It is kept
 * in session storage (never shared across tabs or sessions) and keyed by user,
 * customer, facility, role and module, so a user or role change never restores
 * another user's or role's view. Every access is guarded: storage can be
 * blocked or full, and the page must render the same without it.
 */

export type { RetainedListModule, RetainedListValues };

export type RetainedListScope = {
  userId: string;
  customerId: string;
  facilityId: string;
  role: string;
  module: RetainedListModule;
};

const KEY_PREFIX = "work:retained-list";

export function retainedListStorageKey({
  userId,
  customerId,
  facilityId,
  role,
  module,
}: RetainedListScope) {
  return `${KEY_PREFIX}:${userId}:${customerId}:${facilityId}:${role}:${module}`;
}

const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

/** For `useSyncExternalStore`: storage has no change event within one tab. */
export function subscribeRetainedListState(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The raw retained value, a primitive so it is a stable store snapshot. */
export function readRetainedListSnapshot(
  scope: RetainedListScope,
): string | null {
  try {
    return window.sessionStorage.getItem(retainedListStorageKey(scope));
  } catch {
    return null;
  }
}

/** Keeps only the valid retained keys of a module's search or state object. */
export function pickRetainedListState(
  module: RetainedListModule,
  search: Record<string, unknown> | undefined,
): RetainedListValues {
  return retainedListCodecs[module].pick(search);
}

export function isRetainedListStateEmpty(
  module: RetainedListModule,
  state: RetainedListValues,
): boolean {
  return retainedListCodecs[module].isEmpty(state);
}

export function parseRetainedListState(
  module: RetainedListModule,
  raw: string | null,
): RetainedListValues {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed))
      return {};
    return pickRetainedListState(module, parsed as Record<string, unknown>);
  } catch {
    return {};
  }
}

export function readRetainedListState(
  scope: RetainedListScope,
): RetainedListValues {
  return parseRetainedListState(scope.module, readRetainedListSnapshot(scope));
}

/**
 * Stores the validated list state, or removes the entry when nothing is
 * active. An unchanged value skips the notification so a reader that rewrites
 * what it just read does not loop.
 */
export function writeRetainedListState(
  scope: RetainedListScope,
  state: Record<string, unknown>,
): void {
  const picked = pickRetainedListState(scope.module, state);
  const next = isRetainedListStateEmpty(scope.module, picked)
    ? null
    : JSON.stringify(picked);
  if (readRetainedListSnapshot(scope) === next) return;
  try {
    if (next === null) {
      window.sessionStorage.removeItem(retainedListStorageKey(scope));
    } else {
      window.sessionStorage.setItem(retainedListStorageKey(scope), next);
    }
  } catch {
    // Storage is optional; the list still works without retained state.
  }
  notify();
}

/** Clears every retained list state, for sign-out, user, role or site change. */
export function clearRetainedListState(): void {
  try {
    const storage = window.sessionStorage;
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key?.startsWith(`${KEY_PREFIX}:`)) keys.push(key);
    }
    for (const key of keys) storage.removeItem(key);
  } catch {
    // Storage is optional.
  }
  notify();
}

/** The Assets list keys worth restoring; the open side sheet is not. */
export function pickAssetListSearch(
  search: Record<string, unknown> | undefined,
): AssetTreeSearch {
  return pickRetainedListState("assets", search) as AssetTreeSearch;
}

export function hasAssetListSearch(search: AssetTreeSearch): boolean {
  return !isRetainedListStateEmpty("assets", search);
}

export function readRetainedAssetsSnapshot(
  scope: Omit<RetainedListScope, "module">,
): string | null {
  return readRetainedListSnapshot({ ...scope, module: "assets" });
}

export function parseRetainedAssetsSnapshot(
  raw: string | null,
): AssetTreeSearch {
  return parseRetainedListState("assets", raw) as AssetTreeSearch;
}

export function readRetainedAssetsSearch(
  scope: Omit<RetainedListScope, "module">,
): AssetTreeSearch {
  return parseRetainedAssetsSnapshot(readRetainedAssetsSnapshot(scope));
}

export function writeRetainedAssetsSearch(
  scope: Omit<RetainedListScope, "module">,
  search: AssetTreeSearch,
): void {
  writeRetainedListState({ ...scope, module: "assets" }, search);
}
